import React, { useCallback, useEffect, useState } from "react";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";
import { BarChart3, Calendar, Download, TrendingDown, Lock } from "lucide-react";
import Gate from "@/components/Gate";

/**
 * iter64 Phase C · Reports (spec §22).
 *
 * Aggregate view of orders by type/department/vendor/staff/status +
 * top-line summary (total, open, avg fulfillment).
 *
 * iter70b · Gated sections:
 *   - Seasonal breakdown ← canViewSeasonalReports
 *   - Lost Sales         ← canViewLostSales
 *   - Export CSV button  ← canExportReports
 */
export default function AdminReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/reports/orders", { params });
      setData(data);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const url = `/admin/reports/orders/export.csv${params.toString() ? "?" + params.toString() : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `orders-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-10">
        <section data-testid="admin-reports-page">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline">Reports</div>
              <h1 className="font-serif text-4xl text-[#1A422D] mt-2">Order Reports</h1>
              <p className="mt-2 text-sm text-[#4A4A4A] max-w-2xl">
                Aggregate view of every order created at your club. Filter by date range to focus on a period.
              </p>
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="label-elegant block mb-1">From</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                       data-testid="reports-date-from" className="input-field" />
              </div>
              <div>
                <label className="label-elegant block mb-1">To</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                       data-testid="reports-date-to" className="input-field" />
              </div>
              <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                      data-testid="reports-clear-dates"
                      className="btn-secondary text-xs">Clear</button>
              <Gate
                cap="canExportReports"
                fallback={
                  <button
                    disabled
                    title="Upgrade to Professional to export"
                    data-testid="reports-export-locked"
                    className="inline-flex items-center gap-1.5 border border-[#D9C68A]/50 text-[#7A7A7A] px-3 py-2 text-xs uppercase tracking-[0.22em] cursor-not-allowed"
                  >
                    <Lock size={12} /> Export
                  </button>
                }
              >
                <button
                  onClick={exportCsv}
                  disabled={exporting}
                  data-testid="reports-export-csv"
                  className="inline-flex items-center gap-1.5 bg-[#1A422D] text-[#F9F8F4] px-3 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
                >
                  <Download size={12} /> {exporting ? "Exporting…" : "Export CSV"}
                </button>
              </Gate>
            </div>
          </div>

          {loading ? (
            <div className="mt-10 text-sm text-[#7A7A7A]">Loading reports…</div>
          ) : !data ? (
            <div className="mt-10 p-8 border border-dashed border-[#B59A5A]/40 text-sm text-[#7A7A7A]" data-testid="reports-empty">
              No reporting data available.
            </div>
          ) : (
            <>
              {/* Summary stats */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reports-summary">
                <Stat label="Total Orders" value={data.summary.total_orders} testid="stat-total-orders" />
                <Stat label="Total Value" value={`$${(data.summary.total_value || 0).toLocaleString()}`} testid="stat-total-value" />
                <Stat label="Open Orders" value={data.summary.open_orders} testid="stat-open-orders" />
                <Stat label="Avg Fulfillment" value={
                  data.summary.avg_fulfillment_days != null
                    ? `${data.summary.avg_fulfillment_days} days`
                    : "—"
                } testid="stat-avg-fulfillment" />
              </div>

              <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Breakdown title="By Order Type" rows={data.by_order_type} testid="breakdown-by-order-type" />
                <Breakdown title="By Department" rows={data.by_department} testid="breakdown-by-department" />
                <Breakdown title="By Vendor" rows={data.by_vendor} testid="breakdown-by-vendor" />
                <Breakdown title="By Staff" rows={data.by_staff} testid="breakdown-by-staff" />
                <Breakdown title="By Status" rows={data.by_status} testid="breakdown-by-status" />
              </div>

              <Gate cap="canViewSeasonalReports">
                <div className="mt-10" data-testid="reports-seasonal-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar size={16} className="text-[#B59A5A]" />
                    <h2 className="font-serif text-2xl text-[#1A422D]">Seasonal · Orders by Month</h2>
                  </div>
                  <Breakdown title="By Month (YYYY-MM)" rows={data.by_month || []}
                             testid="breakdown-by-month" showSubtotal />
                </div>
              </Gate>

              <Gate cap="canViewLostSales">
                <div className="mt-10" data-testid="reports-lost-sales-section">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown size={16} className="text-[#A33]" />
                    <h2 className="font-serif text-2xl text-[#1A422D]">Lost Sales · Cancelled Orders</h2>
                  </div>
                  {(data.lost_sales || []).length === 0 ? (
                    <div className="p-5 border border-[#E5E3D8] bg-white text-sm text-[#7A7A7A]"
                         data-testid="lost-sales-empty">
                      No cancelled orders in this period — nothing walked away.
                    </div>
                  ) : (
                    <Breakdown title="By Reason" rows={data.lost_sales} testid="breakdown-lost-sales" showSubtotal />
                  )}
                </div>
              </Gate>
            </>
          )}
        </section>
      </main>
    </>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-5 border border-[#E5E3D8] bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#7A7A7A] font-semibold">{label}</div>
      <div className="mt-2 font-serif text-3xl text-[#1A422D]">{value}</div>
    </div>
  );
}

function Breakdown({ title, rows, testid, showSubtotal = false }) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div className="p-5 border border-[#E5E3D8] bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="text-[#B59A5A]" size={16} />
        <h3 className="font-serif text-lg text-[#1A422D]">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-[#7A7A7A]">No data.</div>
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 12).map((r) => {
            const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
            return (
              <div key={r.key} className="grid grid-cols-12 gap-2 items-center text-xs">
                <div className="col-span-4 truncate text-[#1A422D]">{r.key}</div>
                <div className="col-span-5 h-2 bg-[#F0EDE0] relative">
                  <div className="h-full bg-[#B59A5A]" style={{ width: `${pct}%` }} />
                </div>
                <div className="col-span-1 text-right text-[#4A4A4A]">{r.count}</div>
                <div className="col-span-2 text-right text-[#4A4A4A] font-mono text-[10px]">
                  {showSubtotal && r.subtotal ? `$${Math.round(r.subtotal).toLocaleString()}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
